<template>
  <div class="chatbot-proposals-page" dir="rtl">
    <h1 class="page-title">הצעות צ'אטבוט</h1>

    <div class="tabs-bar">
      <button
        class="tab"
        :class="{ active: activeList === 'clarification' }"
        @click="setList('clarification')"
      >בירור מול יועצת השמה</button>
      <button
        class="tab"
        :class="{ active: activeList === 'chatbot' }"
        @click="setList('chatbot')"
      >הצעות שהועברו לבטר אייג'נטס</button>
      <button
        class="tab"
        :class="{ active: activeList === 'agreements' }"
        @click="setList('agreements')"
      >הסכמות</button>
      <button
        class="tab"
        :class="{ active: activeList === 'all' }"
        @click="setList('all')"
      >הכל</button>

      <div class="status-filter">
        <label>סטטוס:</label>
        <select dir="rtl" v-model="statusFilter" @change="onStatusFilterChange">
          <option value="">הכל</option>
          <option value="pending">ממתין לטיפול</option>
          <option value="handled">טופל</option>
        </select>
      </div>
      <div class="status-filter">
        <label>משרה עם להבה:</label>
        <select dir="rtl" v-model="isHotFilter" @change="onIsHotFilterChange">
          <option value="">הכל</option>
          <option value="true">עם להבה</option>
          <option value="false">ללא להבה</option>
        </select>
      </div>
    </div>

    <div v-if="is_loading" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <table v-else-if="proposals.length" class="proposals-table">
      <thead>
        <tr>
          <th>#</th>
          <th>מועמד</th>
          <th>משרה</th>
          <th>חברה</th>
          <th>פעולת מגייסת</th>
          <th>החלטת בטר אייג'נטס</th>
          <th>סוג</th>
          <th>סטטוס</th>
          <th>תאריך</th>
          <th>טיפול</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in proposals" :key="row.id" :class="{ handled: row.status === 'handled' }">
          <td>{{ row.row_number }}</td>
          <td>
            <link-entity v-if="row.applicant" :payload="row.applicant" />
            <span v-else>—</span>
          </td>
          <td>
            <link-entity v-if="row.position" :payload="row.position" :additional_data="{ resource_type: 'Position' }" />
            <span v-else>{{ row.position_title || '—' }}</span>
          </td>
          <td>
            <link-entity v-if="row.company" :payload="row.company" :additional_data="{ resource_type: 'Company' }" />
            <span v-else>{{ row.company_name || '—' }}</span>
          </td>
          <td>
            <span>{{ recruiterActionLabel(row.recruiter_action) }}</span>
            <div
              v-if="row.recruiter_action === 'reject' && row.rejection_reason_text"
              class="rejection-reason"
            ><span
              v-if="rejectionCategoryLabel(row.rejection_reason_category)"
              class="rejection-reason-category"
            >{{ rejectionCategoryLabel(row.rejection_reason_category) }}: </span>{{ row.rejection_reason_text }}</div>
          </td>
          <td class="decision-cell">
            <span>{{ baDecisionLabel(row.ba_decision) }}</span>
            <small
              v-if="row.ba_decision === 'needs_info' && row.missing_info_summary"
              class="needs-info-summary"
            >
              {{ row.missing_info_summary }}
            </small>
          </td>
          <td>
            <span v-if="row.needs_recruiter_clarification" class="badge badge-clarif">בירור</span>
            <span v-else-if="row.recruiter_ba_agreement" class="badge badge-agreement">הסכמה</span>
            <span v-else class="badge badge-chatbot">צ'אטבוט</span>
          </td>
          <td>
            <select
              class="status-select"
              :class="row.status === 'handled' ? 'status-select-handled' : 'status-select-pending'"
              :value="row.status"
              :disabled="row.updating_status"
              @change="changeStatus(row, $event.target.value)"
            >
              <option value="pending">ממתין לטיפול</option>
              <option value="handled">טופל</option>
            </select>
          </td>
          <td>{{ formatDate(row.created_at) }}</td>
          <td class="handle-cell">
            <div class="handle-actions">
              <button
                class="op-btn op-info"
                :disabled="row.recalculating"
                @click="sendProposalCv(row)"
              >Send cv</button>
              <button
                class="op-btn op-info"
                :disabled="row.recalculating"
                @click="openCvOther(row)"
              >Cv other</button>
              <button
                class="op-btn op-warning"
                :disabled="row.recalculating"
                @click="recalculate(row)"
              >{{ row.recalculating ? '...מחשב' : 'חשב מחדש' }}</button>
              <button class="op-btn op-handle" @click="openHandle(row)">
                {{ row.status === 'handled' ? 'צפייה' : 'טיפול' }}
              </button>
              <button
                v-if="row.status !== 'handled'"
                class="op-btn op-reject"
                :disabled="row.rejecting || row.deferring"
                @click="openReject(row)"
              >{{ row.rejecting ? '...פוסל' : 'פסילה' }}</button>
              <button
                v-if="row.status !== 'handled'"
                class="op-btn op-plain"
                :disabled="row.rejecting || row.deferring"
                :title="'דחייה לשלב מאוחר — ניקוי הפיד בלי לפסול'"
                @click="deferProposal(row)"
              >{{ row.deferring ? '...דוחה' : 'דחה לשלב מאוחר' }}</button>
              <button
                v-if="!row.recruiter_ba_agreement && !row.complexity_note && !row.editing_complex"
                class="op-btn op-complex"
                @click="toggleComplex(row)"
              >זה מסובך</button>
            </div>

            <div v-if="row.editing_complex" class="complex-editor">
              <textarea
                v-model="row.complex_draft"
                dir="rtl"
                rows="3"
                placeholder="מה מסובך כאן? כתבי הסבר מילולי..."
              ></textarea>
              <div class="complex-editor-actions">
                <button
                  class="op-btn op-warning"
                  :disabled="row.saving_complex"
                  @click="saveComplex(row)"
                >{{ row.saving_complex ? '...שומר' : 'שמירה' }}</button>
                <button
                  class="op-btn op-plain"
                  :disabled="row.saving_complex"
                  @click="cancelComplex(row)"
                >ביטול</button>
              </div>
            </div>

            <div v-else-if="row.complexity_note" class="complex-note">
              <div class="complex-note-label">
                סומן כמסובך{{ row.complexity_marked_by ? ' · ' + row.complexity_marked_by : '' }}
              </div>
              <div class="complex-note-text">{{ row.complexity_note }}</div>
              <button class="link-btn" @click="toggleComplex(row)">עריכה</button>
            </div>

            <!-- Disagreement zone: exactly one derived class per row. Case A/C
                 render an inline capture field here; Case B's per-dimension input
                 lives in the handle modal, so it shows a state badge only. -->
            <div
              v-if="row.disagreement_class === 'A' || row.disagreement_class === 'C'"
              class="disagreement-zone"
              :class="row.disagreement_class === 'C' ? 'dz-c' : 'dz-a'"
            >
              <div v-if="row.feedback_state === 'sent'" class="dz-sent">
                ✓ נשלח<span v-if="row.feedback_explanation_by"> · {{ row.feedback_explanation_by }}</span>
              </div>
              <div v-else-if="row.feedback_state === 'task_opened'" class="dz-sent">
                ✓ נפתחה בדיקה<span v-if="row.feedback_explanation_by"> · {{ row.feedback_explanation_by }}</span><span v-if="row.feedback_jira_task_id"> · {{ row.feedback_jira_task_id }}</span>
              </div>
              <template v-else>
                <button
                  v-if="!row.dz_open"
                  class="op-btn op-plain dz-trigger"
                  @click="expandDisagreement(row)"
                >
                  <span class="dz-chev">▸</span>
                  {{ row.disagreement_class === 'C' ? 'המידע קיים במערכת — בדקו שוב' : 'רישום סיבת פסילה' }}
                </button>
                <div v-else class="dz-editor">
                  <div v-if="row.disagreement_class === 'C'" class="dz-context">
                    מה הסוכן דיווח כחסר: {{ row.missing_info_summary }}
                  </div>
                  <label class="dz-label">
                    {{ row.disagreement_class === 'C' ? 'איזה מידע חסר, והיכן הוא נמצא במערכת?' : 'מה הסיבה לפסילה?' }}
                  </label>
                  <textarea
                    class="dz-textarea"
                    dir="rtl"
                    rows="3"
                    v-model="disagreementDrafts[row.id]"
                    :disabled="row.dz_submitting"
                  ></textarea>
                  <span class="dz-helper">
                    {{ row.disagreement_class === 'C' ? 'לדוגמה: השכר מעודכן בכרטיס המשרה / הניסיון מופיע בקו״ח.' : 'בכמה מילים — יעזור למערכת ללמוד.' }}
                  </span>
                  <div class="dz-actions">
                    <button
                      class="op-btn op-warning"
                      :disabled="row.dz_submitting"
                      @click="submitDisagreement(row)"
                    >{{ submitLabel(row) }}</button>
                    <button
                      class="op-btn op-plain"
                      :disabled="row.dz_submitting"
                      @click="cancelDisagreement(row)"
                    >ביטול</button>
                  </div>
                  <div v-if="row.dz_error" class="dz-error">{{ dzErrorMessage(row) }}</div>
                  <div v-if="row.dz_guidance" class="dz-guidance">{{ dzGuidanceMessage(row) }}</div>
                </div>
              </template>
            </div>
            <div
              v-else-if="row.disagreement_class === 'B' && row.feedback_state === 'sent'"
              class="disagreement-zone dz-b"
            >
              <div class="dz-sent">
                ✓ נשלח<span v-if="row.feedback_explanation_by"> · {{ row.feedback_explanation_by }}</span>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-else class="no-results">אין הצעות להצגה</p>

    <div class="pager" v-if="totalPages > 1">
      <button class="button" :disabled="page <= 1" @click="goToPage(page - 1)">← הקודם</button>
      <span class="pager-info">עמוד {{ page }} מתוך {{ totalPages }} ({{ total }})</span>
      <button class="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">הבא →</button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity'
import { bus } from '@/custom/classes/bus.js'
import { rejectionCategoryLabel, shouldAskRejectionReason } from '@/custom/classes/rejection_reasons.js'
import cvOtherMixin from '@/mixins/cvOtherMixin.js'

export default {
  name: 'chatbot-proposals-page',
  mixins: [cvOtherMixin],
  components: {
    LinkEntity
  },
  data () {
    return {
      proposals: [],
      total: 0,
      per: 50,
      page: 1,
      activeList: 'clarification',
      statusFilter: '',
      isHotFilter: '',
      is_loading: false,
      // Case A / Case C capture text, keyed by row id. Separate from any modal
      // state — this feeds the recruiter-feedback / needs-info loop only.
      disagreementDrafts: {}
    }
  },
  computed: {
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.per))
    }
  },
  created () {
    this.fetchProposals()
    bus.$on('chatbot_proposal_handled', this.onHandled)
  },
  beforeDestroy () {
    bus.$off('chatbot_proposal_handled', this.onHandled)
  },
  methods: {
    ...mapMutations('modals', ['create']),
    // Add the row-local UI state the "טיפול" cell needs (operator + "זה מסובך"
    // editing flags) so Vue tracks them reactively.
    decorateRow (row) {
      row.recalculating = false
      row.editing_complex = false
      row.saving_complex = false
      row.complex_draft = ''
      this.$set(row, 'rejecting', false)
      this.$set(row, 'deferring', false)
      // Derived disagreement class, mirroring the server mapping (§1). Exactly one
      // class (or none) per row → at most one new affordance ever appears.
      row.disagreement_class = this.deriveDisagreementClass(row)
      this.$set(row, 'dz_open', false)
      this.$set(row, 'dz_submitting', false)
      this.$set(row, 'dz_error', false)
      this.$set(row, 'dz_guidance', false)
      this.$set(this.disagreementDrafts, row.id, '')
      return row
    },
    // Client-side mirror of Admin::V1::ChatbotProposalsController#derived_disagreement_class.
    deriveDisagreementClass (row) {
      const ra = row.recruiter_action
      const bd = row.ba_decision
      if (ra === 'reject' && bd === 'submit') return 'A'
      if (ra === 'submit' && bd === 'reject') return 'B'
      if (ra === 'submit' && bd === 'needs_info') return 'C'
      // Agreement rows are intentionally quiet: they live under the agreements
      // tab as audit rows and do not need the disagreement feedback affordance.
      return null
    },
    expandDisagreement (row) {
      this.$set(row, 'dz_open', true)
      this.$set(row, 'dz_error', false)
      this.$set(row, 'dz_guidance', false)
    },
    cancelDisagreement (row) {
      this.$set(row, 'dz_open', false)
    },
    submitLabel (row) {
      if (row.disagreement_class === 'C') return row.dz_submitting ? '...פותח' : 'פתיחת בדיקה'
      return row.dz_submitting ? '...שולח' : 'שליחה'
    },
    dzErrorMessage (row) {
      return row.disagreement_class === 'C' ? 'לא ניתן לפתוח בדיקה כרגע — נסי שוב' : 'השליחה נכשלה — נסי שוב'
    },
    dzGuidanceMessage (row) {
      return row.disagreement_class === 'C' ? 'כבר נפתחה בדיקה למשרה זו' : 'כבר נלמד כלל מההסבר הזה'
    },
    // Case A submit (recruiter rejected, BA said fit) — a single neutral reason.
    // Case C is routed to submitNeedsInfo. Nothing here submits the candidate.
    submitDisagreement (row) {
      if (row.dz_submitting) return
      if (row.disagreement_class === 'C') {
        this.submitNeedsInfo(row)
        return
      }
      const explanation = (this.disagreementDrafts[row.id] || '').trim()
      if (!explanation) {
        this.$utils.emit('show_toast', { text: 'כתבי סיבה קצרה לפני השליחה', type: 'error' })
        return
      }
      if (explanation.length > 4000) {
        this.$utils.emit('show_toast', { text: 'ההסבר ארוך מדי (עד 4000 תווים)', type: 'error' })
        return
      }
      this.$set(row, 'dz_submitting', true)
      this.$set(row, 'dz_error', false)
      this.$set(row, 'dz_guidance', false)
      this.$network_utils
        .submitDisagreementFeedback({
          id: row.id,
          direction: 'recruiter_reject_vs_ba_submit',
          explanation: explanation
        })
        .then(response => {
          row.feedback_state = 'sent'
          row.feedback_explanation_by = (response && response.explanation_by) || row.feedback_explanation_by
          this.$set(row, 'dz_open', false)
          this.$utils.emit('show_toast', { text: 'הסיבה נשמרה' })
        })
        .catch(error => {
          this.handleDisagreementError(row, error)
        })
        .finally(() => {
          this.$set(row, 'dz_submitting', false)
        })
    },
    // Case C submit (BA needs_info, recruiter submitted) — opens a data-plumbing
    // investigation via rebase_server → jira. Not a rule; no candidate action.
    submitNeedsInfo (row) {
      if (row.dz_submitting) return
      const text = (this.disagreementDrafts[row.id] || '').trim()
      if (text.length < 10) {
        this.$utils.emit('show_toast', { text: 'כתבי היכן נמצא המידע (לפחות 10 תווים)', type: 'error' })
        return
      }
      this.$set(row, 'dz_submitting', true)
      this.$set(row, 'dz_error', false)
      this.$set(row, 'dz_guidance', false)
      this.$network_utils
        .reportNeedsInfo({ id: row.id, recruiter_text: text })
        .then(response => {
          row.feedback_state = 'task_opened'
          row.feedback_explanation_by = (response && response.explanation_by) || row.feedback_explanation_by
          row.feedback_jira_task_id = (response && response.task && response.task.id) || row.feedback_jira_task_id
          this.$set(row, 'dz_open', false)
          this.$utils.emit('show_toast', { text: 'נפתחה בדיקה' })
        })
        .catch(error => {
          this.handleDisagreementError(row, error)
        })
        .finally(() => {
          this.$set(row, 'dz_submitting', false)
        })
    },
    // 409 is guidance (rule already learned / task already open), not an error
    // state — surface a neutral hint. Everything else is a retryable error.
    handleDisagreementError (row, error) {
      const status = error && error.response && error.response.status
      if (status === 409) {
        this.$set(row, 'dz_guidance', true)
      } else {
        this.$set(row, 'dz_error', true)
      }
    },
    async sendProposalCv (row) {
      if (!row.offer_id) {
        this.$utils.emit('show_toast', { text: 'אין מספיק מידע כדי לפתוח שליחת קו״ח', type: 'error' })
        return
      }
      try {
        const offer = await this.$network_utils.getResourceOffers({ show_id: row.offer_id })
        this.create({
          designator: 'send-cv-mode-selection-modal',
          component_name: 'SendCvModeSelectionModal',
          data: offer,
          modal_title: 'בחירת מצב שליחת קורות חיים',
          modal_width: 600,
          is_center_modal: true
        })
      } catch (error) {
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת ההצעה', type: 'error' })
      }
    },
    openCvOther (row) {
      if (!row.applicant) {
        this.$utils.emit('show_toast', { text: 'אין מועמד משויך להצעה זו', type: 'error' })
        return
      }
      this.clickedCvOther(row)
    },
    recalculate (row) {
      if (row.recalculating) return
      this.$set(row, 'recalculating', true)
      this.$network_utils
        .recalculateChatbotProposal({ id: row.id })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'חישוב מחדש רץ ברקע — רענני בעוד רגע כדי לראות תוצאה מעודכנת' })
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בהפעלת חישוב מחדש', type: 'error' })
        })
        .finally(() => {
          this.$set(row, 'recalculating', false)
        })
    },
    toggleComplex (row) {
      this.$set(row, 'complex_draft', row.complexity_note || '')
      this.$set(row, 'editing_complex', true)
    },
    cancelComplex (row) {
      this.$set(row, 'editing_complex', false)
    },
    saveComplex (row) {
      const note = (row.complex_draft || '').trim()
      if (!note) {
        this.$utils.emit('show_toast', { text: 'כתבי הסבר קצר לפני השמירה', type: 'error' })
        return
      }
      this.$set(row, 'saving_complex', true)
      this.$network_utils
        .markChatbotProposalComplex({ id: row.id, note: note })
        .then(updated => {
          const next = updated || {}
          row.complexity_note = next.complexity_note !== undefined ? next.complexity_note : note
          row.complexity_marked_by = next.complexity_marked_by
          row.complexity_marked_at = next.complexity_marked_at
          this.$set(row, 'editing_complex', false)
          this.$utils.emit('show_toast', { text: 'ההסבר נשמר' })
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בשמירת ההסבר', type: 'error' })
        })
        .finally(() => {
          this.$set(row, 'saving_complex', false)
        })
    },
    setList (list) {
      this.activeList = list
      // Agreement rows are saved as handled audit rows. If the operator was on
      // disagreements with the status filter set to pending, carrying that
      // filter into the agreements tab makes a valid agreements list look empty.
      if (list === 'agreements' && this.statusFilter === 'pending') {
        this.statusFilter = ''
      }
      this.page = 1
      this.fetchProposals()
    },
    fetchProposals () {
      this.is_loading = true
      const params = { page: this.page, per: this.per }
      if (this.activeList !== 'all') {
        params.list = this.activeList
      }
      if (this.statusFilter) {
        params.status = this.statusFilter
      }
      if (this.isHotFilter === 'true' || this.isHotFilter === 'false') {
        params.is_hot = this.isHotFilter
      }
      this.$network_utils
        .getChatbotProposals(params)
        .then(res => {
          const rows = (res && res.data) ? res.data : []
          this.proposals = rows.map(row => this.decorateRow(row))
          this.total = (res && res.total) ? res.total : 0
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בטעינת הצעות צ\'אטבוט', type: 'error' })
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    goToPage (target) {
      if (target < 1 || target > this.totalPages) return
      this.page = target
      this.fetchProposals()
    },
    openHandle (row) {
      this.create({
        designator: 'chatbot-proposal-handle-modal-' + row.id,
        component_name: 'ChatbotProposalHandleModal',
        data: row,
        modal_title: 'טיפול בהצעת צ\'אטבוט',
        modal_width: 640,
        is_center_modal: true
      })
    },
    onHandled () {
      this.fetchProposals()
    },
    // Reject flow. The reason is SAMPLED — ~1 in 3 rejects opens the blocking
    // category+text modal; the rest post action=reject immediately with no
    // reason (the backend accepts a reasonless reject and skips the learning
    // relay). When asked, the modal collects the structured reason and posts
    // action=reject.
    openReject (row) {
      if (row.rejecting || row.deferring) return
      if (!shouldAskRejectionReason()) {
        this.rejectWithoutReason(row)
        return
      }
      this.create({
        designator: 'chatbot-reject-reason-modal-' + row.id,
        component_name: 'ChatbotRejectReasonModal',
        data: row,
        modal_title: 'סיבת פסילה',
        modal_width: 560,
        is_center_modal: true
      })
    },
    // The 2-of-3 no-reason path: reject immediately, no modal.
    rejectWithoutReason (row) {
      this.$set(row, 'rejecting', true)
      this.$network_utils
        .rejectChatbotProposal({ id: row.id })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'ההצעה נפסלה' })
          this.fetchProposals()
        })
        .catch(error => {
          const serverError = error && error.response && error.response.data && error.response.data.error
          this.$utils.emit('show_toast', { text: serverError || 'שגיאה בפסילת ההצעה — נסי שוב', type: 'error' })
        })
        .finally(() => {
          this.$set(row, 'rejecting', false)
        })
    },
    // Defer ("דחה לשלב מאוחר") — feed-cleanup snooze, not a rejection. Sends
    // action=defer; no modal, no reason, never relayed to Better Agents.
    deferProposal (row) {
      if (row.rejecting || row.deferring) return
      this.$set(row, 'deferring', true)
      this.$network_utils
        .deferChatbotProposal({ id: row.id })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'ההצעה נדחתה לשלב מאוחר' })
          this.fetchProposals()
        })
        .catch(error => {
          const serverError = error && error.response && error.response.data && error.response.data.error
          this.$utils.emit('show_toast', { text: serverError || 'שגיאה בדחיית ההצעה', type: 'error' })
        })
        .finally(() => {
          this.$set(row, 'deferring', false)
        })
    },
    changeStatus (row, newStatus) {
      if (!newStatus || newStatus === row.status) {
        return
      }
      const previousStatus = row.status
      this.$set(row, 'updating_status', true)
      this.$network_utils
        .updateChatbotProposalStatus({ id: row.id, status: newStatus })
        .then(updated => {
          const next = updated || {}
          row.status = next.status || newStatus
          row.status_label = next.status_label || (row.status === 'handled' ? 'טופל' : 'ממתין לטיפול')
          row.handled_at = next.handled_at !== undefined ? next.handled_at : row.handled_at
          row.handled_by = next.handled_by !== undefined ? next.handled_by : row.handled_by
          this.$utils.emit('show_toast', { text: 'הסטטוס עודכן בהצלחה' })
          // If a status filter is active and the row no longer matches it, reload
          // the list so the table reflects the current filter.
          if (this.statusFilter && this.statusFilter !== row.status) {
            this.fetchProposals()
          }
        })
        .catch(() => {
          row.status = previousStatus
          this.$utils.emit('show_toast', { text: 'שגיאה בעדכון הסטטוס', type: 'error' })
        })
        .finally(() => {
          this.$set(row, 'updating_status', false)
        })
    },
    recruiterActionLabel (action) {
      if (!action) return '—'
      return action === 'reject' ? 'פסילה' : action === 'submit' ? 'הגשה' : action === 'defer' ? 'דחייה לשלב מאוחר' : action
    },
    // English category KEY -> Hebrew label. Delegates to the shared module that
    // both reject modals and rebase_server's REJECTION_REASON_CATEGORIES mirror.
    rejectionCategoryLabel (key) {
      return rejectionCategoryLabel(key)
    },
    baDecisionLabel (decision) {
      switch (decision) {
        case 'submit': return 'הגשה'
        case 'reject': return 'פסילה'
        case 'needs_info': return 'נדרש מידע נוסף'
        default: return decision || '—'
      }
    },
    formatDate (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    },
    onStatusFilterChange () {
      this.page = 1
      this.fetchProposals()
    },
    onIsHotFilterChange () {
      this.page = 1
      this.fetchProposals()
    }
  }
}
</script>

<style scoped>
.chatbot-proposals-page {
  padding: 20px;
  text-align: right;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 18px;
  color: #2c3e50;
}
.tabs-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.tab {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}
.tab.active {
  background: #3273dc;
  color: #fff;
  border-color: #3273dc;
}
.status-filter {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
.status-filter select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e0;
}
.decision-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.needs-info-summary {
  color: #6b7280;
  font-size: 12px;
}
.rejection-reason {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  white-space: pre-wrap;
}
.rejection-reason-category {
  font-weight: 700;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.proposals-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.proposals-table th,
.proposals-table td {
  padding: 10px 12px;
  font-size: 14px;
  text-align: right;
}
.proposals-table tr {
  border-bottom: 2px solid #cbd5e0;
}
.proposals-table th {
  background: #f1f5f9;
  font-weight: 700;
  color: #2c3e50;
}
.proposals-table tr.handled {
  background: #fafafa;
  color: #7f8c8d;
}
.badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.badge-pending {
  background: #fff3cd;
  color: #856404;
}
.badge-handled {
  background: #d4edda;
  color: #155724;
}
.badge-clarif {
  background: #f8d7da;
  color: #721c24;
}
.badge-chatbot {
  background: #e2e3ff;
  color: #3b3f99;
}
.badge-agreement {
  background: #eafaf1;
  color: #1e7a46;
}
.status-select {
  border-radius: 12px;
  border: 1px solid #cbd5e0;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.status-select:disabled {
  opacity: 0.6;
  cursor: default;
}
.status-select-pending {
  background: #fff3cd;
  color: #856404;
}
.status-select-handled {
  background: #d4edda;
  color: #155724;
}
.handle-cell {
  min-width: 200px;
}
.handle-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.op-btn {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.op-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.op-info {
  border-color: #b6d4fe;
  color: #1565c0;
}
.op-info:hover:not(:disabled) {
  background: #eef5ff;
}
.op-warning {
  border-color: #ffd9a0;
  color: #b9770e;
}
.op-warning:hover:not(:disabled) {
  background: #fff6e9;
}
.op-plain {
  color: #475569;
}
.op-plain:hover:not(:disabled) {
  background: #f1f5f9;
}
/* Primary "טיפול" action — the main entry point into a proposal, so it is
   larger and filled to stand out from the secondary outline buttons. */
.op-handle {
  background: #3273dc;
  border-color: #3273dc;
  color: #fff;
  padding: 8px 20px;
  font-size: 14px;
  box-shadow: 0 1px 2px rgba(50, 115, 220, 0.35);
}
.op-handle:hover:not(:disabled) {
  background: #2760b8;
  border-color: #2760b8;
}
.op-complex {
  border-color: #f5b7b1;
  color: #c0392b;
}
.op-complex:hover:not(:disabled) {
  background: #fdecea;
}
.op-reject {
  border-color: #e74c3c;
  color: #fff;
  background: #e74c3c;
}
.op-reject:hover:not(:disabled) {
  background: #cf3d2e;
  border-color: #cf3d2e;
}
.complex-editor {
  margin-top: 8px;
}
.complex-editor textarea {
  width: 100%;
  border-radius: 6px;
  border: 1px solid #cbd5e0;
  padding: 8px;
  font-size: 13px;
  resize: vertical;
}
.complex-editor-actions {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}
.complex-note {
  margin-top: 8px;
  background: #fdf2f2;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 8px 10px;
}
.complex-note-label {
  font-size: 11px;
  font-weight: 700;
  color: #c0392b;
  margin-bottom: 4px;
}
.complex-note-text {
  font-size: 13px;
  color: #34495e;
  line-height: 1.45;
  white-space: pre-wrap;
}
.link-btn {
  margin-top: 4px;
  background: none;
  border: none;
  padding: 0;
  color: #1565c0;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}
.no-results {
  padding: 30px;
  text-align: center;
  color: #7f8c8d;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
}
.pager-info {
  font-size: 14px;
  color: #555;
}

/* ── Disagreement zone (Case A / Case C) — its own Common Region below the
   op-buttons and the pink complex-note. Indigo family (BA/chatbot) so it never
   merges with the danger-pink note above it. ── */
.disagreement-zone {
  margin-top: 8px;
  padding: 8px 10px;
  border-top: 1px solid #dbe0f5;
  background: #f5f6fe;
  border-radius: 8px;
}
.dz-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dz-chev {
  font-size: 10px;
  line-height: 1;
  color: #3b3f99;
}
.dz-editor {
  margin-top: 8px;
}
.dz-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}
.dz-textarea {
  width: 100%;
  border-radius: 6px;
  border: 1px solid #cbd5e0;
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
}
.dz-context {
  background: #eef0ff;
  border: 1px solid #c9cef5;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.45;
  margin-bottom: 8px;
}
.dz-helper {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}
.dz-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}
.dz-sent {
  display: inline-block;
  margin-top: 4px;
  background: #eafaf1;
  border: 1px solid #a7e9c5;
  color: #1e7a46;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
.dz-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
}
.dz-guidance {
  margin-top: 6px;
  font-size: 12px;
  color: #4b5563;
}
</style>
